/**
 * QMS Page
 * Quality Management System — list, kanban, trends
 */

import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  type CollisionDetection,
} from '@dnd-kit/core';
import { Copy, Download, Settings2, Plus, ChevronDown, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSession } from '@/hooks/useSession';
import { useQmsBoard } from '../hooks/useQmsBoard';
import { useQmsTicket, useUpdateQmsTicket } from '../hooks/useQmsTicket';
import { useQmsFilters } from '../hooks/useQmsFilters';
import { useQmsDragDrop } from '../hooks/useQmsDragDrop';
import { useQmsAssignmentPrompt } from '../hooks/useQmsAssignmentPrompt';
import { useQmsPermissions } from '../hooks/useQmsPermissions';
import {
  useQmsTicketListTable,
  copyQmsTicketsToClipboard,
  exportQmsTicketsCSV,
} from '../hooks/useQmsTicketListTable';
import { useAccounts } from '@/features/installations/hooks/useAccountData';
import { useOwnerOptions } from '@/features/rocks/hooks/useRocks';
import { useTeams } from '@/features/rocks/hooks/useTeams';
import { QmsColumn } from '../components/board/QmsColumn';
import { QmsTicketCard } from '../components/board/QmsTicketCard';
import { QmsFilterBar } from '../components/board/QmsFilterBar';
import { QmsTicketModal } from '../components/ticket/QmsTicketModal';
import { QmsBoardSkeleton } from '../components/board/QmsBoardSkeleton';
import { QmsTicketWizard } from '../components/wizard/QmsTicketWizard';
import { QmsCapaWizard } from '../components/wizard/QmsCapaWizard';
import { QmsClientTicketWizard } from '../components/wizard/QmsClientTicketWizard';
import { QmsTrendSummaryWidget, QmsPatternsPanel } from '../components/trends';
import { formatQmsDepartmentName } from '../components/trends/qmsTrendPresentation';
import { QmsAssignmentPromptDialog } from '../components/shared/QmsAssignmentPromptDialog';
import { QmsTicketListView } from '../components/QmsTicketListView';
import { QmsWorkflowSignatureDialog } from '../components/workflow/QmsWorkflowSignatureDialog';
import { QMS_BOARD_COLUMNS } from '../constants/qmsBoard';
import { getQmsOrganisationId } from '../utils/organisation';
import { isProtectedQualityEvent } from '../utils/workflow';
import type {
  QmsColumn as QmsColumnType,
  QmsTicket,
  QmsTicketStatus,
  QmsTicketType,
} from '../types/qms.types';

type ActiveTab = 'list' | 'board' | 'archive' | 'trends';
type PatternTicketFocus = {
  ticketIds: string[];
  label: string;
};

const TABS: { value: ActiveTab; label: string }[] = [
  { value: 'list', label: 'Tickets' },
  { value: 'board', label: 'Kanban' },
  { value: 'archive', label: 'Archive' },
  { value: 'trends', label: 'Trends' },
];

const ACTIVE_TAB_STORAGE_KEY = 'atek_qms_active_tab';
const VALID_TABS: ActiveTab[] = ['list', 'board', 'archive', 'trends'];

const CLOSED_STATUSES = new Set(['validated_closed', 'closed']);

export function QmsKanbanPage() {
  const { t } = useTranslation('qms');
  const { user, isLoading: userLoading } = useSession();

  // URL-based routing for shareable ticket links
  const { ticketId: urlTicketId } = useParams<{ ticketId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isFullAdmin = (user?.postgres_roles?.includes('full_admin') || user?.postgres_roles?.includes('director')) ?? false;
  const urlTypeFilter = searchParams.get('type');
  const boardTypeFilter = urlTypeFilter === 'ticket' || urlTypeFilter === 'capa' || urlTypeFilter === 'client'
    ? urlTypeFilter
    : undefined;
  const urlAccountFilter = searchParams.get('account') || 'all';
  const useAllOrganisationClientScope = isFullAdmin && boardTypeFilter === 'client';
  // V4 session no longer carries `organisation`. For internal-staff QMS use,
  // default to the internal QMS org so the board fetches the ATEK org's tickets. The
  // 'all' branch (full-admin client view) is unchanged.
  const boardOrganisation = useAllOrganisationClientScope ? 'all' : getQmsOrganisationId(user?.organisation);

  // Active tab
  const [activeTab, setActiveTab] = useState<ActiveTab>(() => {
    const saved = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY) as ActiveTab | null;
    return saved && VALID_TABS.includes(saved) ? saved : 'list';
  });
  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  const [patternTicketFocus, setPatternTicketFocus] = useState<PatternTicketFocus | null>(null);

  const handleTabChange = (tab: ActiveTab) => {
    if (tab !== 'list') {
      setPatternTicketFocus(null);
    }
    setActiveTab(tab);
  };

  // Column definitions for kanban
  const COLUMNS: QmsColumnType[] = useMemo(
    () => QMS_BOARD_COLUMNS.map(({ titleKey, ...column }) => ({
      ...column,
      title: t(titleKey),
    })),
    [t]
  );

  // Data fetching
  const { data: boardData, isLoading, error } = useQmsBoard(boardOrganisation, {
    type: useAllOrganisationClientScope ? 'client' : undefined,
    accountId: urlAccountFilter,
  });

  // Fetch ticket from URL if present (for direct links)
  const {
    data: urlTicketData,
    isLoading: isLoadingUrlTicket,
    error: urlTicketError,
  } = useQmsTicket(urlTicketId || null, {
    enabled: !!urlTicketId,
  });

  // Fetch accounts for filtering — customer accounts only
  const { data: accountsData } = useAccounts({ status: 'active', type: 'customer' });

  // Owner + team option lists for the filter bar (sourced from the
  // Employees directory and the Teams feature, not derived from existing
  // ticket data — so users + teams with zero tickets still appear).
  const { data: ownerOptions = [] } = useOwnerOptions();
  const { data: teamOptions = [] } = useTeams();

  // Filter state and logic (kanban + URL-driven type filter)
  const {
    typeFilter,
    workflowFilter,
    searchQuery,
    assigneeFilter,
    accountFilter,
    departmentFilter,
    setTypeFilter,
    setWorkflowFilter,
    setSearchQuery,
    setAssigneeFilter,
    setAccountFilter,
    setDepartmentFilter,
    filteredBoard,
    pendingCounts,
  } = useQmsFilters({ boardData });

  // Flat ticket list (respects URL filters: type, workflow, assignee, account, department, search)
  const allFilteredTickets = useMemo(
    () => Object.values(filteredBoard).flat(),
    [filteredBoard],
  );

  const allBoardTickets = useMemo(
    () => Object.values(boardData?.columns || {}).flat(),
    [boardData],
  );

  // Partition into open vs closed for Tickets vs Archive tabs
  const ticketsForActiveTab = useMemo(() => {
    if (activeTab === 'list' && patternTicketFocus) {
      const focusIds = new Set(patternTicketFocus.ticketIds);
      return allBoardTickets.filter((ticket) =>
        focusIds.has(ticket._id) || (ticket.ticket_id ? focusIds.has(ticket.ticket_id) : false)
      );
    }

    if (activeTab === 'archive') {
      return allFilteredTickets.filter((t) => CLOSED_STATUSES.has(t.status));
    }
    if (activeTab === 'list') {
      return allFilteredTickets.filter((t) => !CLOSED_STATUSES.has(t.status));
    }
    return allFilteredTickets;
  }, [activeTab, allBoardTickets, allFilteredTickets, patternTicketFocus]);

  // List-table state (column visibility, sort, per-column filters)
  const listTable = useQmsTicketListTable(ticketsForActiveTab);

  const clearListColumnFilters = () => {
    for (const [key, value] of Object.entries(listTable.columnFilters)) {
      if (value) listTable.setColumnFilter(key, '');
    }
  };

  const handleViewPatternTickets = (ticketIds: string[]) => {
    if (ticketIds.length === 0) {
      toast.info('This pattern does not include direct ticket links.');
      return;
    }

    clearListColumnFilters();
    setPatternTicketFocus({
      ticketIds,
      label: `${ticketIds.length} ticket${ticketIds.length === 1 ? '' : 's'} from detected pattern`,
    });
    setActiveTab('list');
  };

  const handleOpenDepartmentPattern = (department: string) => {
    const label = formatQmsDepartmentName(department);
    clearListColumnFilters();
    setPatternTicketFocus(null);
    setActiveTab('list');
    listTable.setColumnFilter('department', JSON.stringify([label]));
  };

  // Assignment prompt for moving unassigned tickets
  const { dialogProps: assignmentDialogProps, isAssigning, promptForAssignment } = useQmsAssignmentPrompt();
  const updateTicketMutation = useUpdateQmsTicket();
  const { canAssignTickets, canChangeTicketStatus, canApproveTicketStatus, canCreateTickets } = useQmsPermissions();
  const [signedTransition, setSignedTransition] = useState<{
    ticket: QmsTicket;
    targetStatus: QmsTicketStatus;
  }>();

  const qmsCollisionDetection: CollisionDetection = useMemo(
    () => (args) => {
      const pointerCollisions = pointerWithin(args);
      if (pointerCollisions.length > 0) return pointerCollisions;
      return rectIntersection(args);
    },
    [],
  );

  // Drag and drop
  const { sensors, activeTicket, isMoving, handleDragStart, handleDragEnd } = useQmsDragDrop({
    columns: COLUMNS,
    filteredBoard,
    organisation: boardOrganisation,
    onBeforeMove: ({ ticket, newStatus, onProceed }) => {
      if (!canChangeTicketStatus) {
        toast.error('You do not have permission to change ticket status.');
        return;
      }
      if (newStatus === 'approved' && newStatus !== ticket.status && !canApproveTicketStatus) {
        toast.error(t('errors.noApproveStatusPermission'));
        return;
      }
      if (isProtectedQualityEvent(ticket)) {
        setSignedTransition({ ticket, targetStatus: newStatus });
        return;
      }
      if (!canAssignTickets) {
        onProceed();
        return;
      }
      promptForAssignment({
        ticket,
        newStatus,
        onProceed: async (assignedUserId) => {
          if (assignedUserId) {
            await updateTicketMutation.mutateAsync({
              ticketId: ticket._id!,
              payload: { assigned_to: assignedUserId },
            });
          }
          onProceed();
        },
      });
    },
  });

  // Modal/wizard state
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [editingTicket, setEditingTicket] = useState<QmsTicket | null>(null);
  const [showTicketWizard, setShowTicketWizard] = useState(false);
  const [showCapaWizard, setShowCapaWizard] = useState(false);
  const [showClientWizard, setShowClientWizard] = useState(false);

  // Open modal when URL ticket is loaded (for direct links)
  useEffect(() => {
    if (urlTicketId && urlTicketData && !isLoadingUrlTicket) {
      setEditingTicket(urlTicketData);
      setShowTicketForm(true);
    } else if (!urlTicketId) {
      setShowTicketForm(false);
      setEditingTicket(null);
    }
  }, [urlTicketId, urlTicketData, isLoadingUrlTicket]);

  // Redirect if URL ticket fails
  useEffect(() => {
    if (urlTicketId && urlTicketError && !isLoadingUrlTicket && !showTicketForm) {
      const params = searchParams.toString();
      navigate(`/qms${params ? `?${params}` : ''}`, { replace: true });
    }
  }, [urlTicketId, urlTicketError, isLoadingUrlTicket, showTicketForm, navigate, searchParams]);

  // Visible kanban columns based on type filter
  const visibleColumns = useMemo(() => {
    if (typeFilter === 'all') return COLUMNS;
    return COLUMNS.filter((col) => col.type === typeFilter);
  }, [typeFilter, COLUMNS]);

  // Create handler
  const handleCreateTicket = (type: QmsTicketType) => {
    if (type === 'ticket') setShowTicketWizard(true);
    else if (type === 'capa') setShowCapaWizard(true);
    else if (type === 'client') setShowClientWizard(true);
  };

  // Edit handler — navigate to URL with ticket ID (preserves filter params)
  const handleEditTicket = (ticket: QmsTicket) => {
    const params = searchParams.toString();
    navigate(`/qms/${ticket._id}${params ? `?${params}` : ''}`);
  };

  const handleCloseTicket = () => {
    const params = searchParams.toString();
    navigate(`/qms${params ? `?${params}` : ''}`);
  };

  const handleCopy = () => {
    copyQmsTicketsToClipboard(listTable.sortedTickets, listTable.columns, listTable.ctx);
    toast.success('Copied to clipboard');
  };

  const handleExportCsv = () => {
    exportQmsTicketsCSV(listTable.sortedTickets, listTable.columns, listTable.ctx);
    toast.success(`Exported ${listTable.sortedTickets.length} rows`);
  };

  const showListToolbar = activeTab === 'list' || activeTab === 'archive';

  // Loading
  if (userLoading || isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2 border-b border-border">
          <div className="flex items-center gap-0">
            {TABS.map((tab) => (
              <div key={tab.value} className="px-4 py-2">
                <div className="h-4 w-16 bg-muted rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
        <QmsBoardSkeleton columnCount={8} cardsPerColumn={3} />
      </div>
    );
  }

  // V4 session has no `organisation` field — the no-org guard is obsolete.
  // QMS is scoped via the internal QMS org default (boardOrganisation above).

  // Error
  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 font-medium mb-2">{t('errors.loadingBoard')}</p>
          <p className="text-gray-600 text-sm">{(error as Error).message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Loading indicator for mutations */}
      {isMoving && (
        <div className="absolute top-0 left-0 right-0 z-10 bg-blue-500 h-1">
          <div className="h-full bg-blue-600 animate-pulse"></div>
        </div>
      )}

      {/* Loading overlay when fetching ticket from URL */}
      {urlTicketId && isLoadingUrlTicket && (
        <div className="fixed inset-0 z-40 bg-white/50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-6 flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-atek-blue"></div>
            <span className="text-gray-700">{t('modal.loadingTicket')}</span>
          </div>
        </div>
      )}

      {/* Tab bar + toolbar */}
      <div className="flex items-center justify-between gap-2 border-b border-border flex-wrap">
        <div className="flex items-center gap-0">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => handleTabChange(tab.value)}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.value
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {showListToolbar && (
          <div className="flex items-center gap-2 flex-wrap pb-1.5">
            <Button variant="outline" size="sm" onClick={handleCopy}>
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCsv}>
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="h-4 w-4 mr-1" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {listTable.columns.filter((c) => !c.noFilter).map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={listTable.isVisible(col.key)}
                    onCheckedChange={() => listTable.toggleColumn(col.key)}
                  >
                    {col.label || col.key}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {canCreateTickets && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-1" />
                    New
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleCreateTicket('ticket')}>
                    Ticket
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleCreateTicket('capa')}>
                    CAPA
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleCreateTicket('client')}>
                    Client Ticket
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {isFullAdmin && (
              <Button asChild variant="outline" size="sm" title={t('settings.title')}>
                <Link to="/qms/settings">
                  <Settings className="h-4 w-4" />
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Tab content */}
      {(activeTab === 'list' || activeTab === 'archive') && (
        <div className="space-y-2">
          {activeTab === 'list' && patternTicketFocus && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm">
              <span className="font-medium text-indigo-950">
                Showing {listTable.sortedTickets.length} of {patternTicketFocus.label}
              </span>
              <button
                type="button"
                onClick={() => setPatternTicketFocus(null)}
                className="text-xs font-semibold text-indigo-700 hover:text-indigo-900"
              >
                Show all tickets
              </button>
            </div>
          )}
          <QmsTicketListView
            sortedTickets={listTable.sortedTickets}
            visibleColumns={listTable.visibleColumns}
            columnFilters={listTable.columnFilters}
            ctx={listTable.ctx}
            sortKey={listTable.sortKey}
            sortDirection={listTable.sortDirection}
            isLoading={isLoading}
            onSort={listTable.handleSort}
            onSetColumnFilter={listTable.setColumnFilter}
            onSelectTicket={handleEditTicket}
            onCreateTicket={
              activeTab === 'list' && canCreateTickets ? () => handleCreateTicket('ticket') : undefined
            }
          />
        </div>
      )}

      {activeTab === 'board' && (
        <div className="flex flex-col">
          <QmsFilterBar
            typeFilter={typeFilter}
            onTypeFilterChange={setTypeFilter}
            workflowFilter={workflowFilter}
            onFilterChange={setWorkflowFilter}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            assigneeFilter={assigneeFilter}
            onAssigneeChange={setAssigneeFilter}
            assignees={ownerOptions}
            accountFilter={accountFilter}
            onAccountChange={setAccountFilter}
            accounts={accountsData?.accounts || []}
            departmentFilter={departmentFilter}
            onDepartmentChange={setDepartmentFilter}
            departments={teamOptions}
            pendingQdCount={pendingCounts.qd_approval}
            pendingCeoCount={pendingCounts.ceo_review}
          />

          <div className="flex-1 overflow-x-auto overflow-y-hidden bg-gray-100 rounded-lg mt-3">
            <DndContext
              sensors={sensors}
              collisionDetection={qmsCollisionDetection}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              accessibility={{
                announcements: {
                  onDragStart() {
                    return t('accessibility.ticketGrabbed');
                  },
                  onDragOver({ over }) {
                    if (over) {
                      const column = COLUMNS.find((c) => c.key === over.id);
                      return t('accessibility.ticketMoved', { column: column?.title || '' });
                    }
                    return '';
                  },
                  onDragEnd({ over }) {
                    if (over) {
                      const column = COLUMNS.find((c) => c.key === over.id);
                      return t('accessibility.ticketDropped', { column: column?.title || '' });
                    }
                    return '';
                  },
                  onDragCancel() {
                    return t('accessibility.dragCancelled');
                  },
                },
              }}
            >
              <div className="flex gap-3 sm:gap-4 p-3 sm:p-6 min-h-[calc(100vh-280px)]">
                {visibleColumns.map((column) => (
                  <QmsColumn
                    key={column.key}
                    column={column}
                    tickets={filteredBoard[column.key] || []}
                    onEditTicket={handleEditTicket}
                  />
                ))}
              </div>

              <DragOverlay>
                {activeTicket ? (
                  <div className="rotate-3 scale-105">
                    <QmsTicketCard ticket={activeTicket} onEdit={() => {}} />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        </div>
      )}

      {activeTab === 'trends' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1">
            <QmsTrendSummaryWidget />
          </div>
          <div className="lg:col-span-2">
            <QmsPatternsPanel
              onViewTickets={handleViewPatternTickets}
              onOpenDepartment={handleOpenDepartmentPattern}
            />
          </div>
        </div>
      )}

      {/* Ticket form modal (for editing) */}
      <QmsTicketModal
        ticket={editingTicket}
        isOpen={showTicketForm}
        onClose={handleCloseTicket}
        onSave={() => {}}
        onDelete={handleCloseTicket}
      />

      {/* Wizards */}
      <QmsTicketWizard
        isOpen={showTicketWizard}
        onClose={() => setShowTicketWizard(false)}
        onTicketCreated={() => setShowTicketWizard(false)}
      />
      <QmsCapaWizard
        isOpen={showCapaWizard}
        onClose={() => setShowCapaWizard(false)}
        onCapaCreated={() => setShowCapaWizard(false)}
      />
      <QmsClientTicketWizard
        isOpen={showClientWizard}
        onClose={() => setShowClientWizard(false)}
        onTicketCreated={() => setShowClientWizard(false)}
      />

      {/* Assignment prompt dialog */}
      <QmsAssignmentPromptDialog
        {...assignmentDialogProps}
        isLoading={isAssigning || updateTicketMutation.isPending}
      />

      {signedTransition && (
        <QmsWorkflowSignatureDialog
          open
          ticket={signedTransition.ticket}
          action="transition"
          targetStatus={signedTransition.targetStatus}
          onOpenChange={(open) => {
            if (!open) setSignedTransition(undefined);
          }}
          onCompleted={() => {
            toast.success('Signed quality-event transition recorded');
            setSignedTransition(undefined);
          }}
        />
      )}
    </div>
  );
}
